/**
 * DriveAuditModal — iter180
 *
 * One-click data-quality check. Runs
 * ``POST /api/admin/drive-config/audit`` which walks every DB column
 * known to hold a Drive path (Email Data.File, Email Data.Picture,
 * Vendor_Shipments file column, Compressor_Files.File/Picture),
 * resolves each through the new path-aware resolver, and reports
 * which paths Drive couldn't find.
 *
 * The dialog opens with a "Run audit" button. While the audit runs
 * we just show a spinner — the request is async + limited to 200
 * rows per source by default so it typically finishes in 10-30s.
 * Results render as a per-source summary table + an expandable list
 * of the missing paths, with a CSV download for the full set.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { X, ListChecks, CheckCircle, Warning, DownloadSimple, Spinner } from "@phosphor-icons/react";
import { auditDrivePaths } from "@/lib/api";


function downloadCsv(filename, rows) {
  const header = ["source", "kind", "file_type", "path", "explanation"];
  const escape = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [header.join(",")].concat(
    rows.map((r) => header.map((h) => escape(r[h])).join(","))
  );
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}


export default function DriveAuditModal({ user, onClose }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [showAllMissing, setShowAllMissing] = useState(false);

  const run = async () => {
    setBusy(true); setErr(""); setResult(null);
    try {
      const r = await auditDrivePaths(user.email, 200);
      setResult(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Audit failed");
    } finally { setBusy(false); }
  };

  const missing = result?.missing_samples || [];
  const visibleMissing = showAllMissing ? missing : missing.slice(0, 25);
  const totals = result?.totals || { checked: 0, ok: 0, missing: 0 };
  const summaryEntries = Object.entries(result?.summary || {});

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center" data-testid="drive-audit-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-sm shadow-xl border border-slate-200 max-h-[85vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <ListChecks size={16} weight="bold" className="text-slate-700" />
            <span className="font-bold text-sm text-slate-900">Audit file paths against Drive</span>
          </div>
          <button data-testid="drive-audit-close"
                   onClick={onClose}
                   className="w-7 h-7 flex items-center justify-center rounded-sm border border-slate-200 hover:bg-slate-100 text-slate-600">
            <X size={13} />
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <p className="text-[12px] text-slate-600">
            Walks every DB column that holds a Drive file path (parts files,
            parts pictures, supplier invoice scans, compressor files &amp;
            pictures), resolves each through the new path-aware resolver,
            and lists any that Drive can&apos;t find — so you can spot bad
            references before users hit them. Limited to <b>200 rows per
            source</b> per run to stay friendly to the Drive API.
          </p>

          {!result && !busy && (
            <Button type="button"
                    data-testid="drive-audit-run"
                    onClick={run}
                    className="rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-9 px-3 text-sm font-semibold inline-flex items-center gap-1.5">
              <ListChecks size={14} weight="bold" /> Run audit
            </Button>
          )}

          {busy && (
            <div className="flex items-center gap-2 text-[13px] text-slate-700"
                  data-testid="drive-audit-running">
              <Spinner size={16} className="animate-spin text-blue-700" />
              Walking DB rows + probing Drive… this typically takes 10–30 seconds.
            </div>
          )}

          {err && (
            <div data-testid="drive-audit-error"
                  className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm px-2 py-1.5">
              {err}
            </div>
          )}

          {result && (
            <div className="space-y-3" data-testid="drive-audit-result">
              {/* Totals */}
              <div className="grid grid-cols-3 gap-2">
                <div className="border border-slate-200 rounded-sm bg-white px-2.5 py-1.5"
                      data-testid="drive-audit-total-checked">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Checked</div>
                  <div className="text-lg font-bold text-slate-900 font-mono">{totals.checked}</div>
                </div>
                <div className="border border-emerald-200 rounded-sm bg-emerald-50 px-2.5 py-1.5"
                      data-testid="drive-audit-total-ok">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Resolved</div>
                  <div className="text-lg font-bold text-emerald-800 font-mono">{totals.ok}</div>
                </div>
                <div className={"border rounded-sm px-2.5 py-1.5 "
                                  + (totals.missing > 0
                                        ? "border-amber-200 bg-amber-50"
                                        : "border-slate-200 bg-slate-50")}
                      data-testid="drive-audit-total-missing">
                  <div className={"text-[10px] uppercase tracking-wider font-bold "
                                    + (totals.missing > 0 ? "text-amber-700" : "text-slate-500")}>Missing</div>
                  <div className={"text-lg font-bold font-mono "
                                    + (totals.missing > 0 ? "text-amber-800" : "text-slate-700")}>{totals.missing}</div>
                </div>
              </div>

              {/* Per-source summary */}
              <div className="overflow-x-auto border border-slate-200 rounded-sm">
                <table className="w-full text-[12px]" data-testid="drive-audit-summary-table">
                  <thead className="bg-slate-50 text-left text-slate-600">
                    <tr>
                      <th className="px-2 py-1.5 font-semibold">Source</th>
                      <th className="px-2 py-1.5 font-semibold text-right">Checked</th>
                      <th className="px-2 py-1.5 font-semibold text-right">OK</th>
                      <th className="px-2 py-1.5 font-semibold text-right">Missing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryEntries.map(([src, s]) => (
                      <tr key={src}
                           data-testid={`drive-audit-summary-row-${src}`}
                           className="border-t border-slate-100">
                        <td className="px-2 py-1.5 font-mono text-slate-700">{src}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{s.checked}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-emerald-700">{s.ok}</td>
                        <td className={"px-2 py-1.5 text-right font-mono "
                                          + (s.missing > 0 ? "text-amber-700 font-bold" : "text-slate-400")}>{s.missing}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Missing samples */}
              {missing.length > 0 && (
                <div className="space-y-1.5" data-testid="drive-audit-missing-list">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1">
                      <Warning size={12} weight="fill" className="text-amber-500" /> Missing paths
                      <span className="text-slate-400 font-normal normal-case">
                        ({missing.length} shown
                        {missing.length === 200 ? "+ — cap reached" : ""}
                        )
                      </span>
                    </div>
                    <Button type="button"
                              data-testid="drive-audit-download"
                              onClick={() => downloadCsv(
                                `drive-audit-missing-${new Date().toISOString().slice(0,10)}.csv`,
                                missing,
                              )}
                              className="rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 h-7 px-2 text-[11px] font-semibold inline-flex items-center gap-1">
                      <DownloadSimple size={11} weight="bold" /> CSV
                    </Button>
                  </div>
                  <div className="border border-slate-200 rounded-sm divide-y divide-slate-100 max-h-[35vh] overflow-y-auto">
                    {visibleMissing.map((m, i) => (
                      <div key={`${m.source}-${i}-${m.path}`}
                            data-testid={`drive-audit-missing-row-${i}`}
                            className="px-2.5 py-1.5 text-[11.5px] flex items-start gap-2">
                        <Warning size={12} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <div className="font-mono text-slate-800 break-all">{m.path}</div>
                          <div className="text-slate-500 mt-0.5">
                            <span className="font-mono">{m.source}</span>
                            {m.file_type && <> · <span>{m.file_type}</span></>}
                            {m.explanation && <> · <span className="italic truncate">{m.explanation}</span></>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {missing.length > 25 && !showAllMissing && (
                    <Button type="button"
                              data-testid="drive-audit-show-all"
                              onClick={() => setShowAllMissing(true)}
                              className="rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 h-7 px-2 text-[11px] font-semibold">
                      Show all {missing.length}
                    </Button>
                  )}
                </div>
              )}

              {missing.length === 0 && totals.checked > 0 && (
                <div className="text-[12px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2 inline-flex items-center gap-2"
                      data-testid="drive-audit-all-resolved">
                  <CheckCircle size={14} weight="fill" className="text-emerald-600" />
                  All {totals.checked} sampled paths resolved cleanly.
                </div>
              )}

              <div className="pt-1">
                <Button type="button"
                          data-testid="drive-audit-rerun"
                          onClick={run}
                          disabled={busy}
                          className="rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 h-8 px-3 text-[12px] font-semibold">
                  Re-run
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
